import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { DeviceTeam, DeviceUser, Device, UpdateDeviceRequest } from '@/api';
import {
    UIFormInput,
    UIFormInputRef,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIPaginatedSelectInput,
    UIPaginatedSelectOption
} from '@/ui';
import { useFetchTeamOptions } from '@/companies/queries';
import { useUpdateDeviceAction } from '../actions';
import { useDevicePermissions, useDevicesTranslation } from '../hooks';
import { useFetchTeamMemberOptions } from '../queries';

type UpdateDeviceModalProps = {
    device: Device;
    isOpen: boolean;
    onClose: () => void;
};

export function UpdateDeviceModal(props: UpdateDeviceModalProps): ReactElement {
    const { device, isOpen, onClose } = props;

    const deviceId = device.id;
    const companyId = device.companyId ?? '';

    const { updateDevice, loading } = useUpdateDeviceAction({
        deviceId
    });

    // Which teams the device may be assigned to. Company owners/admins can pick
    // any team; a team admin can only assign to the teams they administer. Other
    // teams are still listed but disabled with an explanation.
    //
    // Scoped to the device's company rather than the selected one, because this
    // modal is also reachable from the standalone device page, which sits
    // outside the company context.
    const { allowedTeamIds } = useDevicePermissions({ companyId });

    function handleClose() {
        onClose();
    }

    function onSubmit(data: UpdateDeviceRequest) {
        updateDevice(data)
            .then(() => {
                handleClose();
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            <UpdateDeviceModalForm
                isOpen={isOpen}
                companyId={companyId}
                onClose={handleClose}
                loading={loading}
                onSubmit={onSubmit}
                nameDefault={device.name}
                teamDefault={device.team}
                userDefault={device.user}
                allowedTeamIds={allowedTeamIds}
            />
        </UIModal>
    );
}

type UpdateDeviceModalFormProps = {
    isOpen: boolean;
    companyId: string;
    nameDefault: string;
    teamDefault?: DeviceTeam | null;
    userDefault?: DeviceUser | null;
    /**
     * Team ids the device may be assigned to; other teams are shown disabled.
     * `null` means every team is allowed.
     */
    allowedTeamIds: string[] | null;
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: UpdateDeviceRequest) => void;
};

function UpdateDeviceModalForm(
    props: UpdateDeviceModalFormProps
): ReactElement {
    const { isOpen } = props;

    if (isOpen) {
        return <FormContent {...props} />;
    } else {
        return <></>;
    }
}

function FormContent(props: UpdateDeviceModalFormProps): ReactElement {
    const {
        companyId,
        nameDefault,
        teamDefault,
        userDefault,
        allowedTeamIds,
        loading,
        onClose,
        onSubmit
    } = props;
    const { t } = useDevicesTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Name
     */
    const nameRef = useRef<UIFormInputRef>(null);
    const [name, setName] = useState(nameDefault);
    const [nameError, setNameError] = useState('');
    const checkName = useCallback(
        (name: string): string => {
            const nameTrimmed = name.trim();
            if (nameTrimmed.length === 0) {
                return t('shared.fields.name.error_required');
            } else if (nameTrimmed.length > 1000) {
                return t('shared.fields.name.error_too_long');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setNameError(checkName(name));
        }
    }, [name, showRealTimeErrors, checkName]);

    /**
     * Team
     */
    // Seeded from the device's current team, which may not appear in the first
    // page of options — the picker keeps the supplied label either way.
    const [team, setTeam] = useState<UIPaginatedSelectOption | undefined>(
        teamDefault
            ? { label: `${teamDefault.name}`, value: teamDefault.teamId }
            : undefined
    );
    const [teamError, setTeamError] = useState('');

    // Answered from my own team roles rather than the loaded page, so it holds
    // for any team id whether or not it is in the current window.
    const isTeamAllowed = useCallback(
        (teamId: string) =>
            allowedTeamIds === null || allowedTeamIds.includes(teamId),
        [allowedTeamIds]
    );

    const teamPicker = useFetchTeamOptions({ companyId });
    const teamOptions = useMemo<UIPaginatedSelectOption[]>(
        () =>
            teamPicker.options.map((option) => {
                const disabled = !isTeamAllowed(option.value);
                return {
                    ...option,
                    disabled,
                    subLabel: disabled
                        ? t('shared.fields.team.disabled_not_admin')
                        : undefined
                };
            }),
        [teamPicker.options, isTeamAllowed, t]
    );

    // No existence check against the options: the device's current team may sit
    // on a page that was never loaded, and rejecting it would fail an otherwise
    // untouched submit. The server rejects anything that slips through.
    const checkTeam = useCallback(
        (selectedTeam: UIPaginatedSelectOption | undefined): string => {
            if (!selectedTeam) {
                return t('shared.fields.team.error_required');
            }

            if (!isTeamAllowed(selectedTeam.value)) {
                return t('shared.fields.team.error_not_allowed');
            }

            return '';
        },
        [isTeamAllowed, t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTeamError(checkTeam(team));
        }
    }, [team, showRealTimeErrors, checkTeam]);

    /**
     * User
     */
    // Seeded from the device's current member, who may not appear in the first
    // page of options — the picker keeps the supplied label either way.
    const [user, setUser] = useState<UIPaginatedSelectOption | undefined>(
        userDefault
            ? {
                  label: userDefault.joined
                      ? `${userDefault.firstName} ${userDefault.lastName}`
                      : userDefault.email,
                  value: userDefault.companyUserId,
                  image: userDefault.image ?? undefined
              }
            : undefined
    );

    // No client-side membership check: the options come back already scoped to
    // the selected team, and changing the team clears the selection. The server
    // rejects anything that slips through.
    const memberOptions = useFetchTeamMemberOptions({
        companyId,
        teamId: team?.value
    });

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkName(name) && !checkTeam(team)) {
            setShowRealTimeErrors(false);
            onSubmit?.({
                name,
                teamId: team!.value,
                companyUserId: user?.value ?? null
            });
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <UIModalHeader
                    title={t('components.update_device_modal.title')}
                />
                <UIModalContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <UIFormInput
                            ref={nameRef}
                            name="name"
                            label={t('shared.fields.name.label')}
                            value={name}
                            description={t('shared.devices.name_description')}
                            type="text"
                            autoComplete="on"
                            errorText={showRealTimeErrors ? nameError : ''}
                            required
                            maxLength={1000}
                            onChange={setName}
                            autoFocus
                        />

                        <UIPaginatedSelectInput
                            label={t('shared.fields.team.label')}
                            placeholder={t('shared.fields.team.placeholder')}
                            description={t('shared.devices.team_description')}
                            searchPlaceholder={t(
                                'shared.fields.team.search_placeholder'
                            )}
                            emptyText={t('shared.fields.team.empty')}
                            errorStateText={t('shared.fields.team.load_error')}
                            required
                            options={teamOptions}
                            value={team}
                            onChange={(value) => {
                                // A member only belongs to the team they were
                                // picked from, so the selection can't survive a
                                // team change.
                                setTeam(value);
                                setUser(undefined);
                            }}
                            search={teamPicker.search}
                            onSearchChange={teamPicker.setSearch}
                            isLoading={teamPicker.status === 'pending'}
                            isError={teamPicker.status === 'error'}
                            isFetchingNextPage={teamPicker.isFetchingNextPage}
                            hasNextPage={teamPicker.hasNextPage}
                            onLoadMore={teamPicker.fetchNextPage}
                            onRetry={teamPicker.refetch}
                            errorText={showRealTimeErrors ? teamError : ''}
                        />

                        {team && (
                            <UIPaginatedSelectInput
                                label={t('shared.fields.member.label_optional')}
                                placeholder={t(
                                    'shared.fields.member.placeholder'
                                )}
                                description={t(
                                    'shared.fields.member.description'
                                )}
                                searchPlaceholder={t(
                                    'shared.fields.member.search_placeholder'
                                )}
                                emptyText={t('shared.fields.member.empty')}
                                errorStateText={t(
                                    'shared.fields.member.load_error'
                                )}
                                clearText={t('shared.fields.member.clear')}
                                value={user}
                                onChange={setUser}
                                options={memberOptions.options}
                                search={memberOptions.search}
                                onSearchChange={memberOptions.setSearch}
                                isLoading={memberOptions.status === 'pending'}
                                isError={memberOptions.status === 'error'}
                                isFetchingNextPage={
                                    memberOptions.isFetchingNextPage
                                }
                                hasNextPage={memberOptions.hasNextPage}
                                onLoadMore={memberOptions.fetchNextPage}
                                onRetry={memberOptions.refetch}
                            />
                        )}
                    </div>
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'submit',
                            loading: loading,
                            disabled: loading,
                            text: t('shared.labels.submit')
                        },
                        {
                            text: t('shared.labels.cancel'),
                            disabled: loading,
                            onClick: onClose
                        }
                    ]}
                />
            </form>
        </>
    );
}
